import { ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon, EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { EyeIcon } from "@heroicons/react/24/outline";
import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from "../../Catalyst/table";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "../../Catalyst/dropdown";
import { Checkbox } from "../../Catalyst/checkbox";
import { getUserStatusDisplay, USER_STATUS } from "../../common/userStatus";
import { ROLE_DISPLAY } from "../GlobalSearch/utils";
import { UserSortKey } from "../../../constants/users";
import { UserV3 } from "../../../store/slices/UsersV3Slice";
import { getOrganizationName } from "../Organizations/utils";
import { formatLastLogin, getUserDisplayName, isRowSelected, RowSelection, UserSort } from "./utils";

// headerClass lets the long "Number of Sessions" label wrap so all eight
// columns fit at the 1440px design width without horizontal scrolling.
const COLUMNS: { key: UserSortKey; label: string; headerClass?: string }[] = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "organization", label: "Organization" },
  { key: "lastLogin", label: "Last Login" },
  { key: "role", label: "Role" },
  { key: "status", label: "Status" },
  { key: "sessionCount", label: "Number of Sessions", headerClass: "w-[130px] whitespace-normal" },
];

// Deactivated rows read red end to end and invited rows are dimmed, per the
// design; the status cell carries the status colour for every row.
const getRowTextClass = (status: string | null) => {
  if (status === USER_STATUS.DEACTIVATED) return "text-red-500";
  if (status === USER_STATUS.INVITED) return "text-texthead opacity-50";
  return "text-texthead";
};

const SortIndicator = ({ direction }: { direction: "asc" | "desc" | null }) => {
  if (direction === "asc") return <ChevronUpIcon className="h-4 w-4 text-texthead" aria-hidden="true" />;
  if (direction === "desc") return <ChevronDownIcon className="h-4 w-4 text-texthead" aria-hidden="true" />;
  return <ChevronUpDownIcon className="h-4 w-4 opacity-40" aria-hidden="true" />;
};

// Org name links to the org in the hierarchy (ADM-93); plain text for roles
// that cannot open the hierarchy pages.
const OrganizationCell = ({
  organization,
  onOpen,
}: {
  organization: UserV3["organization"];
  onOpen: ((organizationId: number) => void) | null;
}) => {
  if (!organization) return <span className="text-textColor">—</span>;
  const name = getOrganizationName(organization);
  if (!onOpen) {
    return (
      <span className="block max-w-[150px] truncate text-texthead" title={name}>
        {name}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(organization.id)}
      title={name}
      className="block max-w-[150px] truncate text-left font-medium text-blue hover:underline"
    >
      {name}
    </button>
  );
};

interface GlobalUsersTableProps {
  users: UserV3[];
  sort: UserSort;
  onSort: (key: UserSortKey) => void;
  // Selection is hidden entirely for read-only roles (Coach).
  selectable: boolean;
  selection: RowSelection;
  onToggleRow: (user: UserV3) => void;
  onTogglePage: (selected: boolean) => void;
  onOpenOrganization: ((organizationId: number) => void) | null;
  onViewActivity: (user: UserV3) => void;
}

const GlobalUsersTable = ({
  users,
  sort,
  onSort,
  selectable,
  selection,
  onToggleRow,
  onTogglePage,
  onOpenOrganization,
  onViewActivity,
}: GlobalUsersTableProps) => {
  const selectedOnPage = users.filter((user) => isRowSelected(selection, user.id)).length;
  const allOnPageSelected = users.length > 0 && selectedOnPage === users.length;

  return (
    <Table>
      <TableHead>
        <TableRow className="bg-[#F9FAFB] border border-x-[#EAECF0]">
          {selectable && (
            <TableHeader className="w-10">
              {/* Catalyst draws the dash only for checked + indeterminate, so
                  "checked" means "any selected"; a partial page click selects all. */}
              <Checkbox
                color="green"
                checked={selectedOnPage > 0}
                indeterminate={selectedOnPage > 0 && !allOnPageSelected}
                onChange={() => onTogglePage(!allOnPageSelected)}
                aria-label="Select all users on this page"
              />
            </TableHeader>
          )}
          {COLUMNS.map((column) => {
            const direction = sort?.by === column.key ? sort.dir : null;
            return (
              <TableHeader
                key={column.key}
                className={column.headerClass}
                aria-sort={direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "none"}
              >
                <button
                  type="button"
                  onClick={() => onSort(column.key)}
                  className="inline-flex items-center gap-1 text-left font-medium hover:text-texthead"
                >
                  {column.label}
                  <SortIndicator direction={direction} />
                </button>
              </TableHeader>
            );
          })}
          <TableHeader className="text-right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((user) => {
          const name = getUserDisplayName(user);
          const status = getUserStatusDisplay(user.status, user.statusDate, "short");
          const rowTextClass = getRowTextClass(user.status);
          return (
            <TableRow key={user.id}>
              {selectable && (
                <TableCell>
                  <Checkbox
                    color="green"
                    checked={isRowSelected(selection, user.id)}
                    onChange={() => onToggleRow(user)}
                    aria-label={`Select ${name}`}
                  />
                </TableCell>
              )}
              {/* Long names/emails are truncated (full value on hover) so one row
                  cannot push the Actions column off-screen. */}
              <TableCell className={`font-medium ${rowTextClass}`}>
                <span className="block max-w-[150px] truncate" title={name}>
                  {name}
                </span>
              </TableCell>
              <TableCell className={rowTextClass}>
                <span className="block max-w-[170px] truncate" title={user.email}>
                  {user.email}
                </span>
              </TableCell>
              <TableCell>
                <OrganizationCell organization={user.organization} onOpen={onOpenOrganization} />
              </TableCell>
              <TableCell className={rowTextClass}>{formatLastLogin(user.lastLoginAt)}</TableCell>
              <TableCell className={rowTextClass}>{ROLE_DISPLAY[user.role] ?? user.role}</TableCell>
              <TableCell>
                <span className={`inline-flex items-center gap-2 text-sm font-medium ${status.textClass}`}>
                  {status.text}
                  <span className={`h-2 w-2 rounded-full ${status.dotClass}`} aria-hidden="true" />
                </span>
              </TableCell>
              <TableCell className={rowTextClass}>{user.sessionCount ?? 0}</TableCell>
              <TableCell className="text-right">
                <Dropdown>
                  <DropdownButton outline aria-label={`Actions for ${name}`}>
                    <EllipsisHorizontalIcon />
                  </DropdownButton>
                  <DropdownMenu anchor="bottom end">
                    {/* The profile route needs the user's org; without one there is nothing to open. */}
                    <DropdownItem
                      disabled={!user.organization}
                      className="disabled:opacity-50"
                      onClick={() => onViewActivity(user)}
                    >
                      <EyeIcon />
                      <DropdownLabel>View Activity</DropdownLabel>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default GlobalUsersTable;
